import * as React from "react";
import { useMemo } from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { CPTCode } from "services/cpt-codes";

import { renderCPTCodeOption } from "./cpt-code-option";
import { useCPTCodeSearch } from "./use-cpt-code-search.hook";

type CPTCodesMultiFieldProps = {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  placeholder?: string;
  // Exclude these CPT code ids from the dropdown options (e.g., the
  // primary CPT code, to prevent duplicate selection).
  excludeIds?: number[];
  // Collapse selected chips beyond this count into "+N" while the field is
  // not focused (MUI Autocomplete passthrough) — for compact/grid cells.
  limitTags?: number;
  // Custom chip rendering (MUI Autocomplete passthrough) — e.g. to mark the
  // first selected code as the primary one.
  renderTags?: React.ComponentProps<typeof AutocompleteFormField>["renderTags"];
};

export type RhfCPTCodesMultiField<T extends FieldValues> =
  CPTCodesMultiFieldProps & UseControllerProps<T>;

export default function CPTCodesMultiField<T extends FieldValues>({
  control,
  required,
  disabled,
  name,
  label = "CPT or HCPCS Codes",
  sx = {},
  placeholder = "Search CPT or HCPCS Code by code or description",
  excludeIds,
  limitTags,
  renderTags
}: RhfCPTCodesMultiField<T>) {
  const { options, isLoading, search, setSearch } = useCPTCodeSearch();

  const filteredOptions = useMemo(() => {
    if (!excludeIds?.length) return options as CPTCode[];
    const excluded = new Set(excludeIds);
    return (options as CPTCode[]).filter((o) => !excluded.has(o.id));
  }, [options, excludeIds]);

  return (
    <AutocompleteFormField
      isLoading={isLoading}
      required={required}
      disabled={disabled}
      openOnFocus
      multiple
      disableCloseOnSelect
      name={name}
      label={label}
      options={filteredOptions}
      noOptionsText={
        !search?.length
          ? "Search CPT or HCPCS Code by code or description"
          : "No options"
      }
      getOptionLabel={({ code }: CPTCode) => code}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      placeholder={placeholder}
      limitTags={limitTags}
      renderTags={renderTags}
      renderOption={renderCPTCodeOption}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onTextInputChange={(text) => {
        setSearch(text);
      }}
      sx={sx}
    />
  );
}
